import { useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Modal, Text } from '@mantine/core';
import { confettiFireAtom } from '@/utils/atoms';
import styles from './ConfettiOverlay.module.css';

// The 20 SVGs in /public/ICP/confetti are sequential frames of one confetti
// burst falling down the screen. We play them as a flipbook.
const FRAME_COUNT = 20;
const FRAME_DURATION_MS = 130; // slower playback for a more relaxed drop (~2.6s total)

// Keep the welcome message on screen ~1s longer than the confetti drop.
const MESSAGE_EXTRA_MS = 1000;

const framePath = (index: number) => `/ICP/confetti/variant${index}.svg`;

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Full-viewport, non-interactive overlay that plays a one-shot confetti drop
 * whenever `confettiFireAtom.count` increments. Mounted once at the root route
 * so it survives navigation and can be triggered from any page by incrementing
 * the counter (e.g. `setConfettiFire(p => ({ count: p.count + 1 }))`).
 *
 * When a fire also carries a `message`, a centered Mantine `Modal` is shown
 * with the message text followed by a confetti emoji. The modal opens with the
 * drop and stays ~1s longer than the confetti before auto-dismissing.
 */
export default function ConfettiOverlay() {
    const fire = useAtomValue(confettiFireAtom);
    const [frameIndex, setFrameIndex] = useState(0); // 0 = not playing
    const [message, setMessage] = useState<string | null>(null);
    const [modalOpened, setModalOpened] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const messageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const preloadedRef = useRef(false);

    // Preload the frames during idle time so the flipbook plays without flicker
    // and everything is HTTP-cached before the user ever triggers it.
    useEffect(() => {
        if (preloadedRef.current || typeof window === 'undefined') return;
        preloadedRef.current = true;

        const preload = () => {
            for (let i = 1; i <= FRAME_COUNT; i++) {
                const img = new Image();
                img.src = framePath(i);
            }
        };

        const ric = (window as any).requestIdleCallback as
            | ((cb: () => void) => number)
            | undefined;
        if (ric) {
            ric(preload);
        } else {
            setTimeout(preload, 1000);
        }
    }, []);

    // Play the flipbook whenever the trigger increments (skip the initial 0).
    useEffect(() => {
        if (fire.count === 0) return;
        if (prefersReducedMotion()) return;

        // Snapshot the message for this fire; open the modal if one is present
        // so it appears alongside the confetti.
        const nextMessage = fire.message ?? null;
        setMessage(nextMessage);
        setModalOpened(!!nextMessage);

        // Clear any pending message timer from a previous fire so it can't close
        // the modal for this new one.
        if (messageTimerRef.current) {
            clearTimeout(messageTimerRef.current);
            messageTimerRef.current = null;
        }

        setFrameIndex(1);
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setFrameIndex((current) => {
                if (current >= FRAME_COUNT) {
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    intervalRef.current = null;

                    // Keep the message on screen ~1s past the confetti, then
                    // close the modal and clear the message so it never leaks
                    // into a later message-less trigger.
                    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
                    messageTimerRef.current = setTimeout(() => {
                        setModalOpened(false);
                        // setMessage(null);
                        messageTimerRef.current = null;
                    }, MESSAGE_EXTRA_MS);

                    return 0; // hide + reset
                }
                return current + 1;
            });
        }, FRAME_DURATION_MS);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
        };
    }, [fire.count]);

    // Clean up any pending message timer on unmount.
    useEffect(() => {
        return () => {
            if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
        };
    }, []);

    return (
        <>
            {frameIndex !== 0 && (
                <div className={styles.overlay} aria-hidden="true">
                    <img className={styles.frame} src={framePath(frameIndex)} alt="" />
                </div>
            )}

            <Modal
                opened={!!message && modalOpened}
                onClose={() => {
                    setModalOpened(false)
                    setMessage(null);
                }}
                centered
                withCloseButton={false}
                closeOnClickOutside={false}
                closeOnEscape={false}
                transitionProps={{ transition: 'fade-down', duration: 600 }}
                withOverlay={false}
                keepMounted={false}
            >
                <Text ta="center" fw={700} size="xl" py={30} px={'sm'}>
                    {message} 🎉
                </Text>
            </Modal>
        </>
    );
};
